import { useState } from 'react';
import { ArrowLeft, Upload, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '@/contexts/CartContext';
import { useOrder } from '@/contexts/OrderContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const { orderType, customerDetails, calculatePackagingCharges, setPaymentProof } = useOrder();
  const navigate = useNavigate();
  const [paymentMode, setPaymentMode] = useState('upi');
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [placing, setPlacing] = useState(false);

  const subtotal = getCartTotal();
  const packagingCharges = calculatePackagingCharges(cart);
  const total = subtotal + packagingCharges;

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload-payment-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedFile(response.data);
      setPaymentProof(response.data.url);
      toast.success('Payment proof uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload payment proof');
    } finally {
      setUploading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (paymentMode === 'upi' && !uploadedFile) {
      toast.error('Please upload payment proof');
      return;
    }

    setPlacing(true);

    try {
      const orderData = {
        order_type: orderType,
        customer_name: customerDetails.name,
        customer_phone: customerDetails.phone,
        customer_address: customerDetails.address || '',
        delivery_instructions: customerDetails.instructions || '',
        items: cart.map(item => ({
          item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category
        })),
        subtotal: subtotal,
        packaging_charges: packagingCharges,
        total_amount: total,
        payment_mode: paymentMode,
        payment_proof_url: uploadedFile?.url || null
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      clearCart();
      toast.success('Order placed successfully!');
      
      // Automatically open WhatsApp to notify restaurant
      if (response.data.whatsapp_url) {
        // Small delay to ensure order confirmation page loads first
        setTimeout(() => {
          window.open(response.data.whatsapp_url, '_blank');
        }, 1500);
      }
      
      navigate(`/order-confirmation/${response.data.id}`);
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-amber-500/30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            onClick={() => navigate('/checkout')}
            variant="ghost"
            className="text-amber-400 hover:bg-amber-500/10"
            data-testid="back-to-checkout-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-amber-400 font-['Playfair_Display']" data-testid="payment-title">Payment</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <Card className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
          <h2 className="text-xl font-bold text-amber-400 mb-4">Choose Payment Method</h2>
          
          <div className="space-y-4">
            <Card 
              className={`p-4 cursor-pointer transition-all ${paymentMode === 'upi' ? 'bg-amber-500/20 border-2 border-amber-500' : 'bg-gray-700/50 border border-gray-600'}`}
              onClick={() => setPaymentMode('upi')}
              data-testid="upi-payment-option"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white">UPI Payment</h3>
                  <p className="text-sm text-gray-300">Scan QR & upload payment proof</p>
                </div>
                {paymentMode === 'upi' && <Check className="w-6 h-6 text-amber-400" />}
              </div>
            </Card>

            <Card 
              className={`p-4 cursor-pointer transition-all ${paymentMode === 'cash' ? 'bg-amber-500/20 border-2 border-amber-500' : 'bg-gray-700/50 border border-gray-600'}`}
              onClick={() => setPaymentMode('cash')}
              data-testid="cash-payment-option"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white">Cash on {orderType === 'delivery' ? 'Delivery' : 'Pickup'}</h3>
                  <p className="text-sm text-gray-300">Pay when you receive your order</p>
                </div>
                {paymentMode === 'cash' && <Check className="w-6 h-6 text-amber-400" />}
              </div>
            </Card>
          </div>
        </Card>

        {paymentMode === 'upi' && (
          <Card className="p-6 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
            <h2 className="text-xl font-bold text-amber-400 mb-4">Scan QR Code to Pay</h2>
            
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-white p-4 rounded-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cart-sync-2/artifacts/kow88k1f_image_123650291.JPG" 
                  alt="UPI QR Code" 
                  className="w-64 h-64 object-contain"
                />
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-amber-200 font-semibold text-lg">Total Amount: ₹{total}</p>
                <p className="text-gray-400 text-sm">Scan with any UPI app to pay</p>
              </div>

              <div className="w-full max-w-md">
                <label 
                  htmlFor="payment-proof" 
                  className="block w-full cursor-pointer"
                >
                  <Card className="p-6 bg-gray-700/50 border-2 border-dashed border-amber-500/50 hover:border-amber-500 transition-colors text-center">
                    {uploadedFile ? (
                      <div className="space-y-2">
                        <Check className="w-12 h-12 mx-auto text-green-400" />
                        <p className="text-green-400 font-semibold">Payment proof uploaded!</p>
                        <p className="text-sm text-gray-400">{uploadedFile.filename}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-12 h-12 mx-auto text-amber-400" />
                        <p className="text-amber-200 font-semibold">
                          {uploading ? 'Uploading...' : 'Upload Payment Screenshot'}
                        </p>
                        <p className="text-sm text-gray-400">Click to select file</p>
                      </div>
                    )}
                  </Card>
                </label>
                <input
                  id="payment-proof"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="payment-proof-input"
                />
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 bg-gradient-to-br from-amber-500 to-orange-600 border-0 shadow-xl">
          <div className="space-y-3 text-white">
            <div className="flex justify-between text-lg">
              <span>Subtotal</span>
              <span data-testid="payment-subtotal">₹{subtotal}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span>Packaging Charges</span>
              <span data-testid="payment-packaging">₹{packagingCharges}</span>
            </div>
            <div className="border-t-2 border-white/30 pt-3 flex justify-between text-2xl font-bold">
              <span>Total</span>
              <span data-testid="payment-total">₹{total}</span>
            </div>
            <Button
              onClick={handlePlaceOrder}
              disabled={placing || (paymentMode === 'upi' && !uploadedFile)}
              className="w-full bg-black hover:bg-gray-900 text-white font-bold py-6 text-lg rounded-xl mt-4"
              data-testid="place-order-button"
            >
              {placing ? 'Placing Order...' : 'Place Order'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
